// frontend/src/pages/EditarPerfil/EditarPerfil.jsx

import React, { useState, useEffect } from "react";
import Template from "../../../components/Template";
import api from "../../../services/api";
import ConfirmationModal from "../../../components/ConfirmationModal";
import SuccessModal from "../../../components/SuccessModal";
import { useAuth } from "../../../context/AuthContext";
import config from "../../../config";

const EditarPerfil = () => {
    const { auth } = useAuth()
    // Estado para armazenar os dados do usuário
    const [usuario, setUsuario] = useState({
        nome: "",
        email: "",
        foto: null, // Arquivo da foto
        previewFoto: "", // URL para pré-visualização da foto
        senha: "",
    });

    // Estados para controle do formulário
    const [isEditMode, setIsEditMode] = useState(false);

    // Modais
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    // Estados de erro
    const [errors, setErrors] = useState({});

    // Carregar os dados do usuário ao montar o componente
    useEffect(() => {
        fetchUsuario();
    }, []);

    const fetchUsuario = async () => {
        try {
            const response = await api.get(`/users/me/${auth.user.id}`); // Endpoint para obter dados do usuário autenticado
            setUsuario({
                nome: response.data.nome || "",
                email: response.data.email || "",
                foto: null,
                previewFoto: response.data.photoUrl ? config.urlArquivo(response.data.photoUrl) : "",
                senha: "",
            });
        } catch (error) {
            console.error("Erro ao carregar dados do usuário:", error);
            alert("Erro ao carregar seus dados. Tente novamente.");
        }
    };

    // Manipulador de mudança nos inputs
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUsuario({ ...usuario, [name]: value });
    };

    // Manipulador de mudança na foto
    const handleFotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUsuario({
                ...usuario,
                foto: file,
                previewFoto: URL.createObjectURL(file),
            });
        }
    };

    // Validação do formulário
    const validateForm = () => {
        const newErrors = {};

        if (!usuario.nome.trim()) {
            newErrors.nome = "Nome é obrigatório";
        }

        if (usuario.email && !validateEmail(usuario.email)) {
            newErrors.email = "Email inválido";
        }

        if (usuario.senha && !isPasswordStrong(usuario.senha)) {
            newErrors.senha = "A senha deve ter pelo menos 8 caracteres, incluindo letras, números e caracteres especiais";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    // Função para validar Email
    const validateEmail = (email) => {
        const regex = /\S+@\S+\.\S+/;
        return regex.test(email);
    };

    // Função para validar a força da senha
    const isPasswordStrong = (password) => {
        // Pelo menos 8 caracteres, com letras, números e caracteres especiais
        const regex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
        return regex.test(password);
    };

    // Submeter o formulário
    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            setShowConfirmDialog(true);
        }
    };

    // Confirmar atualização
    const handleConfirmSubmit = async () => {
        setShowConfirmDialog(false);
        try {
            const formData = new FormData();
            formData.append("nome", usuario.nome);
            formData.append("email", usuario.email);
            if (usuario.senha) {
                formData.append("senha", usuario.senha);
            }
            if (usuario.foto) {
                formData.append("foto", usuario.foto);
            }

            await api.put("/users/editar/profile", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            setShowSuccessDialog(true);
            fetchUsuario(); // Atualizar os dados após a edição
            setUsuario((prev) => ({ ...prev, senha: "" })); // Limpar o campo de senha
        } catch (error) {
            console.error("Erro ao atualizar perfil:", error);
            alert("Erro ao atualizar seu perfil. Verifique os dados e tente novamente.");
        }
    };

    const handleCancelSubmit = () => {
        setShowConfirmDialog(false);
    };

    return (
        <Template>
            <div className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título da Página */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">Editar Perfil</h1>
                </div>

                {/* Formulário de Edição */}
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4 bg-white p-6 rounded-md border border-slate-200"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Foto de Perfil */}
                        <div className="flex flex-col gap-1.5 items-center">
                            <label className="text-sm text-slate-700 font-medium">Foto de Perfil</label>
                            {usuario.previewFoto ? (
                                <img
                                    src={usuario.previewFoto}
                                    alt="Foto de Perfil"
                                    className="w-32 h-32 rounded-full object-cover"
                                />
                            ) : (
                                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center">
                                    <span className="text-gray-500">Sem Foto</span>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                name="foto"
                                onChange={handleFotoChange}
                                className="mt-2"
                            />
                        </div>

                        {/* Nome */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Nome</label>
                            <input
                                type="text"
                                name="nome"
                                value={usuario.nome}
                                onChange={handleInputChange}
                                className={`px-3 py-2 border ${errors.nome ? "border-red-600" : "border-slate-300"
                                    } rounded-md text-sm`}
                                placeholder="Digite seu nome"
                                required
                            />
                            {errors.nome && <p className="text-red-600 text-sm">{errors.nome}</p>}
                        </div>

                        {/* Email */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Email</label>
                            <input
                                type="email"
                                name="email"
                                value={usuario.email}
                                onChange={handleInputChange}
                                className={`px-3 py-2 border ${errors.email ? "border-red-600" : "border-slate-300"
                                    } rounded-md text-sm`}
                                placeholder="Digite seu email"
                                required
                                disabled // Email geralmente não é editável; remova se quiser permitir edição
                            />
                            {errors.email && <p className="text-red-600 text-sm">{errors.email}</p>}
                        </div>

                        {/* Senha */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">
                                Senha
                                <span className="text-gray-500 text-xs ml-1">(Deixe vazio para não alterar)</span>
                            </label>
                            <input
                                type="password"
                                name="senha"
                                value={usuario.senha}
                                onChange={handleInputChange}
                                className={`px-3 py-2 border ${errors.senha ? "border-red-600" : "border-slate-300"
                                    } rounded-md text-sm`}
                                placeholder="Digite uma nova senha"
                            />
                            {errors.senha && <p className="text-red-600 text-sm">{errors.senha}</p>}
                        </div>
                    </div>

                    {/* Botões do Formulário */}
                    <div className="flex items-center gap-4">
                        <button
                            type="submit"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md"
                        >
                            Salvar Alterações
                        </button>
                    </div>
                </form>

                {/* Modal de Confirmação */}
                <ConfirmationModal
                    isVisible={showConfirmDialog}
                    onConfirm={handleConfirmSubmit}
                    onCancel={handleCancelSubmit}
                    message="Deseja salvar as alterações no seu perfil?"
                    textButtonConfirm="Sim, Salvar"
                />

                {/* Modal de Sucesso */}
                <SuccessModal
                    isVisible={showSuccessDialog}
                    message="Perfil atualizado com sucesso!"
                    onClose={() => setShowSuccessDialog(false)}
                />
            </div>
        </Template>
    );
};

export default EditarPerfil;
